import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
  Switch,
  Chip,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import InboxIcon from "@mui/icons-material/Inbox";
import { toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import api from "../../axiosInstance";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import MentorshipValidation from "../Validations/MentorshipValidation";

const ACCENT = "#17479e";

// Panel wrapper: rounded card with a header strip (icon, title, count, add button)
function PanelHeader({ icon, title, count, onAdd, addDisabled }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #eef0f4",
        backgroundColor: "#fbfcfe",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${ACCENT}, #2f66c9)`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {count}
          </Typography>
        </Box>
      </Box>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={onAdd}
        disabled={addDisabled}
        {...BUTTON_STYLES.primary}
      >
        Add
      </Button>
    </Box>
  );
}

// Empty state used inside both list panels
function EmptyState({ icon, text }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.5,
        py: 3,
        px: 1,
        color: "text.secondary",
        textAlign: "left",
      }}
    >
      {icon}
      <Typography variant="body2" sx={{ textAlign: "left" }}>
        {text}
      </Typography>
    </Box>
  );
}

// A subject-category row: initial avatar, selected accent, hover-reveal actions
function CoreSubjectRow({ row, selected, onSelect, onEdit, onDelete }) {
  return (
    <Box
      onClick={onSelect}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.1,
        mb: 0.75,
        borderRadius: 2,
        cursor: "pointer",
        border: "1px solid",
        borderColor: selected ? ACCENT : "transparent",
        backgroundColor: selected ? "#eef3fc" : "transparent",
        transition: "all 0.15s",
        "&:hover": { backgroundColor: selected ? "#eef3fc" : "#f5f7fa" },
        "&:hover .row-actions": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: selected ? ACCENT : "#eef1f6",
          color: selected ? "#fff" : "#667",
          fontWeight: 700,
          fontSize: "0.85rem",
          flexShrink: 0,
        }}
      >
        {(row.subjectName || "?").charAt(0).toUpperCase()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontWeight: 600, fontSize: "0.87rem", color: "#1a1a2e" }}
        >
          {row.subjectName}
        </Typography>
        {row.active === false && (
          <Typography
            variant="caption"
            sx={{ color: "#c62828", fontWeight: 600 }}
          >
            Inactive
          </Typography>
        )}
      </Box>
      <Box
        className="row-actions"
        sx={{
          display: "flex",
          gap: 0.25,
          opacity: 0.5,
          transition: "opacity 0.15s",
        }}
      >
        <Tooltip title="Edit" arrow>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" arrow>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <ChevronRightIcon
        sx={{ color: selected ? ACCENT : "#d5d8de", fontSize: 20 }}
      />
    </Box>
  );
}

// A grouped-subject row: status dot, hover-reveal actions
function GroupedSubjectRow({ row, onEdit, onDelete }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.1,
        mb: 0.75,
        borderRadius: 2,
        border: "1px solid #f0f1f4",
        transition: "all 0.15s",
        "&:hover": { borderColor: "#dbe4f5", backgroundColor: "#fafbfd" },
        "&:hover .row-actions": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: row.active === false ? "#d5d8de" : "#43a047",
          flexShrink: 0,
        }}
      />
      <Typography
        noWrap
        sx={{ flex: 1, fontSize: "0.86rem", fontWeight: 500, color: "#1a1a2e" }}
      >
        {row.groupSubjectTitle}
      </Typography>
      {row.active === false && (
        <Chip
          label="Inactive"
          size="small"
          sx={{ height: 20, fontSize: "0.65rem" }}
        />
      )}
      <Box
        className="row-actions"
        sx={{
          display: "flex",
          gap: 0.25,
          opacity: 0.5,
          transition: "opacity 0.15s",
        }}
      >
        <Tooltip title="Edit" arrow>
          <IconButton size="small" color="primary" onClick={onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" arrow>
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// Active/inactive toggle used in both add/edit dialogs
function ActiveToggleRow({ checked, onChange }) {
  return (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1.25,
        borderRadius: 2,
        border: "1px solid #eef0f4",
        backgroundColor: "#fbfcfe",
      }}
    >
      <Box>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
          Active
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Inactive items are hidden from mentor-facing filters
        </Typography>
      </Box>
      <Switch checked={checked} onChange={onChange} />
    </Box>
  );
}

function MentorSubjects() {
  const [coreSubjects, setCoreSubjects] = useState([]);
  const [isLoadingCore, setIsLoadingCore] = useState(false);
  const [coreSearch, setCoreSearch] = useState("");
  const [selectedCoreSubject, setSelectedCoreSubject] = useState(null);

  const [groupedSubjects, setGroupedSubjects] = useState([]);
  const [isLoadingGrouped, setIsLoadingGrouped] = useState(false);
  const [groupedSearch, setGroupedSearch] = useState("");

  const [error, setError] = useState(null);

  // Core Subject add/edit dialog
  const [coreDialogOpen, setCoreDialogOpen] = useState(false);
  const [coreEditing, setCoreEditing] = useState(null);
  const [coreDeleteId, setCoreDeleteId] = useState(null);

  // Grouped Subject add/edit dialog
  const [groupedDialogOpen, setGroupedDialogOpen] = useState(false);
  const [groupedEditing, setGroupedEditing] = useState(null);
  const [groupedDeleteId, setGroupedDeleteId] = useState(null);

  // Fetch core subjects (real endpoint, already used by MentorAdminTable filters)
  const loadCoreSubjects = () => {
    setIsLoadingCore(true);
    api
      .get("/mentor-catalog/coreSubjects")
      .then((res) => setCoreSubjects(res.data?.data ?? []))
      .catch((err) => {
        console.error("Core subjects fetch error:", err);
        setError("Failed to load subject categories.");
      })
      .finally(() => setIsLoadingCore(false));
  };

  useEffect(() => {
    loadCoreSubjects();
  }, []);

  // Fetch grouped subjects for the selected core subject (real endpoint)
  const loadGroupedSubjects = (coreSubjectId) => {
    if (!coreSubjectId) {
      setGroupedSubjects([]);
      return;
    }
    setIsLoadingGrouped(true);
    api
      .get("/mentor-catalog/groupedSubjects", {
        params: { core_subject_id: coreSubjectId },
      })
      .then((res) => setGroupedSubjects(res.data?.data ?? []))
      .catch((err) => {
        console.error("Grouped subjects fetch error:", err);
        setError("Failed to load subjects.");
      })
      .finally(() => setIsLoadingGrouped(false));
  };

  useEffect(() => {
    loadGroupedSubjects(selectedCoreSubject?.id);
  }, [selectedCoreSubject]);

  const filteredCoreSubjects = coreSubjects.filter((c) =>
    (c.subjectName || "").toLowerCase().includes(coreSearch.toLowerCase()),
  );
  const filteredGroupedSubjects = groupedSubjects.filter((g) =>
    (g.groupSubjectTitle || "")
      .toLowerCase()
      .includes(groupedSearch.toLowerCase()),
  );

  // ---- Core Subject CRUD ----
  const openAddCore = () => {
    setCoreEditing(null);
    setCoreDialogOpen(true);
  };
  const openEditCore = (row) => {
    setCoreEditing(row);
    setCoreDialogOpen(true);
  };

  const coreFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      subjectName: coreEditing?.subjectName || "",
      active: coreEditing?.active ?? true,
    },
    validationSchema: MentorshipValidation.CoreSubjectValidationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const payload = {
          subjectName: values.subjectName.trim(),
          active: values.active,
        };
        if (coreEditing?.id) {
          await api.put(
            `/mentor-catalog/core-subjects/${coreEditing.id}`,
            payload,
          );
          toast.success("Subject category updated!");
        } else {
          await api.post(`/mentor-catalog/core-subjects`, payload);
          toast.success("Subject category created!");
        }
        setCoreDialogOpen(false);
        resetForm();
        loadCoreSubjects();
      } catch (err) {
        console.error("Core subject save error:", err);
        toast.error("Failed to save subject category. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleDeleteCore = async () => {
    try {
      await api.delete(`/mentor-catalog/core-subjects/${coreDeleteId}`);
      toast.success("Subject category deleted!");
      if (selectedCoreSubject?.id === coreDeleteId)
        setSelectedCoreSubject(null);
      loadCoreSubjects();
    } catch (err) {
      console.error("Core subject delete error:", err);
      toast.error("Failed to delete subject category. Please try again.");
    } finally {
      setCoreDeleteId(null);
    }
  };

  // ---- Grouped Subject CRUD ----
  const openAddGrouped = () => {
    if (!selectedCoreSubject) {
      toast.warning("Select a subject category first");
      return;
    }
    setGroupedEditing(null);
    setGroupedDialogOpen(true);
  };
  const openEditGrouped = (row) => {
    setGroupedEditing(row);
    setGroupedDialogOpen(true);
  };

  const groupedFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      groupSubjectTitle: groupedEditing?.groupSubjectTitle || "",
      coreSubjectId:
        groupedEditing?.coreSubjectId || selectedCoreSubject?.id || "",
      active: groupedEditing?.active ?? true,
    },
    validationSchema: MentorshipValidation.GroupedSubjectValidationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const payload = {
          groupSubjectTitle: values.groupSubjectTitle.trim(),
          coreSubjectId: values.coreSubjectId,
          active: values.active,
        };
        if (groupedEditing?.id) {
          await api.put(
            `/mentor-catalog/grouped-subjects/${groupedEditing.id}`,
            payload,
          );
          toast.success("Subject updated!");
        } else {
          await api.post(`/mentor-catalog/grouped-subjects`, payload);
          toast.success("Subject created!");
        }
        setGroupedDialogOpen(false);
        resetForm();
        loadGroupedSubjects(selectedCoreSubject.id);
      } catch (err) {
        console.error("Grouped subject save error:", err);
        toast.error("Failed to save subject. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleDeleteGrouped = async () => {
    try {
      await api.delete(`/mentor-catalog/grouped-subjects/${groupedDeleteId}`);
      toast.success("Subject deleted!");
      loadGroupedSubjects(selectedCoreSubject.id);
    } catch (err) {
      console.error("Grouped subject delete error:", err);
      toast.error("Failed to delete subject. Please try again.");
    } finally {
      setGroupedDeleteId(null);
    }
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2.5,
        }}
      >
        {/* Core Subjects (categories) */}
        <Paper
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: "column",
            maxHeight: "75vh",
          }}
        >
          <PanelHeader
            icon={<CategoryIcon sx={{ color: "#fff", fontSize: 20 }} />}
            title="Core Subjects"
            count={`${coreSubjects.length} total`}
            onAdd={openAddCore}
          />
          <Box sx={{ p: 2, overflow: "auto" }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search categories..."
              value={coreSearch}
              onChange={(e) => setCoreSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            {isLoadingCore ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredCoreSubjects.length === 0 ? (
              <EmptyState
                icon={<InboxIcon sx={{ fontSize: 32, color: "#ccc" }} />}
                text="No subject categories found"
              />
            ) : (
              filteredCoreSubjects.map((row) => (
                <CoreSubjectRow
                  key={row.id}
                  row={row}
                  selected={selectedCoreSubject?.id === row.id}
                  onSelect={() => setSelectedCoreSubject(row)}
                  onEdit={() => openEditCore(row)}
                  onDelete={() => setCoreDeleteId(row.id)}
                />
              ))
            )}
          </Box>
        </Paper>

        {/* Grouped Subjects for the selected category */}
        <Paper
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: "column",
            maxHeight: "75vh",
          }}
        >
          <PanelHeader
            icon={<MenuBookIcon sx={{ color: "#fff", fontSize: 20 }} />}
            title={
              selectedCoreSubject
                ? `Grouped Subjects · ${selectedCoreSubject.subjectName}`
                : "Grouped Subjects"
            }
            count={
              selectedCoreSubject
                ? `${groupedSubjects.length} total`
                : "Select a category"
            }
            onAdd={openAddGrouped}
            addDisabled={!selectedCoreSubject}
          />
          <Box sx={{ p: 2, overflow: "auto" }}>
            {!selectedCoreSubject ? (
              <EmptyState
                icon={<TouchAppIcon sx={{ fontSize: 32, color: "#ccc" }} />}
                text="Select a subject category to view its subjects"
              />
            ) : (
              <>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search subjects..."
                  value={groupedSearch}
                  onChange={(e) => setGroupedSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 1.5,
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
                {isLoadingGrouped ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : filteredGroupedSubjects.length === 0 ? (
                  <EmptyState
                    icon={<InboxIcon sx={{ fontSize: 32, color: "#ccc" }} />}
                    text="No subjects found"
                  />
                ) : (
                  filteredGroupedSubjects.map((row) => (
                    <GroupedSubjectRow
                      key={row.id}
                      row={row}
                      onEdit={() => openEditGrouped(row)}
                      onDelete={() => setGroupedDeleteId(row.id)}
                    />
                  ))
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Add/Edit Core Subject dialog */}
      <Dialog
        open={coreDialogOpen}
        onClose={() => setCoreDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box component="form" onSubmit={coreFormik.handleSubmit}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              fontWeight: 700,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${ACCENT}, #2f66c9)`,
              }}
            >
              <CategoryIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            {coreEditing ? "Edit Subject Category" : "Add Subject Category"}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2.5 }}>
            <TextField
              autoFocus
              fullWidth
              margin="normal"
              name="subjectName"
              label="Category Name"
              value={coreFormik.values.subjectName}
              onChange={coreFormik.handleChange}
              onBlur={coreFormik.handleBlur}
              error={
                coreFormik.touched.subjectName &&
                Boolean(coreFormik.errors.subjectName)
              }
              helperText={
                coreFormik.touched.subjectName && coreFormik.errors.subjectName
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <ActiveToggleRow
              checked={coreFormik.values.active}
              onChange={(e) =>
                coreFormik.setFieldValue("active", e.target.checked)
              }
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setCoreDialogOpen(false)}
              {...BUTTON_STYLES.secondary}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={coreFormik.isSubmitting}
              {...BUTTON_STYLES.primary}
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Add/Edit Grouped Subject dialog */}
      <Dialog
        open={groupedDialogOpen}
        onClose={() => setGroupedDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box component="form" onSubmit={groupedFormik.handleSubmit}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              fontWeight: 700,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${ACCENT}, #2f66c9)`,
              }}
            >
              <MenuBookIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            {groupedEditing ? "Edit Subject" : "Add Subject"}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2.5 }}>
            <TextField
              autoFocus
              fullWidth
              margin="normal"
              name="groupSubjectTitle"
              label="Subject Name"
              value={groupedFormik.values.groupSubjectTitle}
              onChange={groupedFormik.handleChange}
              onBlur={groupedFormik.handleBlur}
              error={
                groupedFormik.touched.groupSubjectTitle &&
                Boolean(groupedFormik.errors.groupSubjectTitle)
              }
              helperText={
                groupedFormik.touched.groupSubjectTitle &&
                groupedFormik.errors.groupSubjectTitle
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Autocomplete
              options={coreSubjects}
              getOptionLabel={(option) => option.subjectName || ""}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={
                coreSubjects.find(
                  (c) => c.id === groupedFormik.values.coreSubjectId,
                ) || null
              }
              onChange={(_, selected) =>
                groupedFormik.setFieldValue("coreSubjectId", selected?.id || "")
              }
              onBlur={() =>
                groupedFormik.setFieldTouched("coreSubjectId", true)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subject Category"
                  margin="normal"
                  fullWidth
                  error={
                    groupedFormik.touched.coreSubjectId &&
                    Boolean(groupedFormik.errors.coreSubjectId)
                  }
                  helperText={
                    groupedFormik.touched.coreSubjectId &&
                    groupedFormik.errors.coreSubjectId
                  }
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              )}
            />
            <ActiveToggleRow
              checked={groupedFormik.values.active}
              onChange={(e) =>
                groupedFormik.setFieldValue("active", e.target.checked)
              }
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setGroupedDialogOpen(false)}
              {...BUTTON_STYLES.secondary}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={groupedFormik.isSubmitting}
              {...BUTTON_STYLES.primary}
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmations */}
      <DeleteConfirmationDialog
        open={!!coreDeleteId}
        onClose={() => setCoreDeleteId(null)}
        onConfirm={handleDeleteCore}
        title="Confirm Delete"
        message="Are you sure you want to delete this subject category? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <DeleteConfirmationDialog
        open={!!groupedDeleteId}
        onClose={() => setGroupedDeleteId(null)}
        onConfirm={handleDeleteGrouped}
        title="Confirm Delete"
        message="Are you sure you want to delete this subject? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default MentorSubjects;
